import pluralize from "pluralize";
import { z } from "zod";
import { Color } from "../constants/colors.js";
const outcomeDefinitionType = {
  preset: "preset",
  custom: "custom"
};
const outcomeResultType = {
  greenRedYellow: "green_red_yellow"
};
const greenRedYellowResultTypeCategory = {
  green: "green",
  red: "red",
  yellow: "yellow",
  gray: "gray"
};
const greenRedYellowResultCategorySchema = z.enum([
  greenRedYellowResultTypeCategory.green,
  greenRedYellowResultTypeCategory.red,
  greenRedYellowResultTypeCategory.yellow,
  greenRedYellowResultTypeCategory.gray
]);
const outcomeResultSchema = z.object({
  category: greenRedYellowResultCategorySchema,
  value: z.string().min(1),
  label: z.string().min(1)
});
const outcomeDefinitionBaseSchema = z.object({
  name: z.string().min(1),
  result_type: z.literal(outcomeResultType.greenRedYellow),
  context: z.string(),
  results: z.array(outcomeResultSchema).min(1),
  // Optional per-playbook overrides for the labels used when referring to the
  // outcome's positive (green) result across dashboards, charts, tables,
  // summaries, and coaching views. When unset, the noun/rate labels fall back
  // to the preset/derived defaults (see getOutcomeNoun, getOutcomePluralNoun,
  // and buildOutcomeGreenRateLabel).
  singular_noun: z.string().min(1).optional(),
  plural_noun: z.string().min(1).optional(),
  rate_label: z.string().min(1).optional()
});
const outcomeGreenRedYellowResultCategoryColor = Object.freeze({
  [greenRedYellowResultTypeCategory.green]: Color.Green,
  [greenRedYellowResultTypeCategory.red]: Color.Red,
  [greenRedYellowResultTypeCategory.yellow]: Color.Yellow,
  [greenRedYellowResultTypeCategory.gray]: Color.Gray
});
const winLossOutcomeDefinitionId = "win_loss";
const winLossOutcomeDefinitionLabel = "Win/Loss";
const winLossOutcomeResultValueId = {
  won: "won",
  lost: "lost",
  unqualified: "unqualified",
  notApplicable: "not_applicable"
};
const winLossOutcomeResultValue = {
  [winLossOutcomeResultValueId.won]: `${winLossOutcomeDefinitionId}:${winLossOutcomeResultValueId.won}`,
  [winLossOutcomeResultValueId.lost]: `${winLossOutcomeDefinitionId}:${winLossOutcomeResultValueId.lost}`,
  [winLossOutcomeResultValueId.unqualified]: `${winLossOutcomeDefinitionId}:${winLossOutcomeResultValueId.unqualified}`,
  [winLossOutcomeResultValueId.notApplicable]: `${winLossOutcomeDefinitionId}:${winLossOutcomeResultValueId.notApplicable}`
};
const winLossOutcomeResultLabel = {
  [winLossOutcomeResultValueId.won]: "Won",
  [winLossOutcomeResultValueId.lost]: "Lost",
  [winLossOutcomeResultValueId.unqualified]: "Unqualified",
  [winLossOutcomeResultValueId.notApplicable]: "N/A"
};
const winLossOutcomeNoun = "win";
const insuranceEnrollmentOutcomeDefinitionId = "insurance_enrollment";
const insuranceEnrollmentOutcomeDefinitionName = "Insurance Enrollment";
const insuranceEnrollmentOutcomeResultValueId = {
  enrolled: "enrolled",
  didNotEnroll: "did_not_enroll",
  unqualified: "unqualified",
  notApplicable: "not_applicable"
};
const insuranceEnrollmentOutcomeResultValue = {
  [insuranceEnrollmentOutcomeResultValueId.enrolled]: `${insuranceEnrollmentOutcomeDefinitionId}:${insuranceEnrollmentOutcomeResultValueId.enrolled}`,
  [insuranceEnrollmentOutcomeResultValueId.didNotEnroll]: `${insuranceEnrollmentOutcomeDefinitionId}:${insuranceEnrollmentOutcomeResultValueId.didNotEnroll}`,
  [insuranceEnrollmentOutcomeResultValueId.unqualified]: `${insuranceEnrollmentOutcomeDefinitionId}:${insuranceEnrollmentOutcomeResultValueId.unqualified}`,
  [insuranceEnrollmentOutcomeResultValueId.notApplicable]: `${insuranceEnrollmentOutcomeDefinitionId}:${insuranceEnrollmentOutcomeResultValueId.notApplicable}`
};
const insuranceEnrollmentOutcomeResultLabel = {
  [insuranceEnrollmentOutcomeResultValueId.enrolled]: "Enrolled",
  [insuranceEnrollmentOutcomeResultValueId.didNotEnroll]: "Did Not Enroll",
  [insuranceEnrollmentOutcomeResultValueId.unqualified]: "Unqualified",
  [insuranceEnrollmentOutcomeResultValueId.notApplicable]: "N/A"
};
const insuranceEnrollmentOutcomeNoun = "enrollment";
const insuranceApplicationOutcomeDefinitionId = "insurance_application";
const insuranceApplicationOutcomeDefinitionName = "Insurance Application";
const insuranceApplicationOutcomeResultValueId = {
  applied: "applied",
  didNotApply: "did_not_apply",
  uninsurable: "uninsurable",
  transferred: "transferred",
  notApplicable: "not_applicable"
};
const insuranceApplicationOutcomeResultValue = {
  [insuranceApplicationOutcomeResultValueId.applied]: `${insuranceApplicationOutcomeDefinitionId}:${insuranceApplicationOutcomeResultValueId.applied}`,
  [insuranceApplicationOutcomeResultValueId.didNotApply]: `${insuranceApplicationOutcomeDefinitionId}:${insuranceApplicationOutcomeResultValueId.didNotApply}`,
  [insuranceApplicationOutcomeResultValueId.uninsurable]: `${insuranceApplicationOutcomeDefinitionId}:${insuranceApplicationOutcomeResultValueId.uninsurable}`,
  [insuranceApplicationOutcomeResultValueId.transferred]: `${insuranceApplicationOutcomeDefinitionId}:${insuranceApplicationOutcomeResultValueId.transferred}`,
  [insuranceApplicationOutcomeResultValueId.notApplicable]: `${insuranceApplicationOutcomeDefinitionId}:${insuranceApplicationOutcomeResultValueId.notApplicable}`
};
const insuranceApplicationOutcomeResultLabel = {
  [insuranceApplicationOutcomeResultValueId.applied]: "Applied",
  [insuranceApplicationOutcomeResultValueId.didNotApply]: "Did Not Apply",
  [insuranceApplicationOutcomeResultValueId.uninsurable]: "Uninsurable",
  [insuranceApplicationOutcomeResultValueId.transferred]: "Transferred",
  [insuranceApplicationOutcomeResultValueId.notApplicable]: "N/A"
};
const insuranceApplicationOutcomeNoun = "application";
const mortgageApplicationOutcomeDefinitionId = "mortgage_application";
const mortgageApplicationOutcomeDefinitionName = "Mortgage Application";
const mortgageApplicationOutcomeResultValueId = {
  applicationSubmitted: "application_submitted",
  applicationNotSubmitted: "application_not_submitted",
  unqualified: "unqualified",
  notApplicable: "not_applicable"
};
const mortgageApplicationOutcomeResultValue = {
  [mortgageApplicationOutcomeResultValueId.applicationSubmitted]: `${mortgageApplicationOutcomeDefinitionId}:${mortgageApplicationOutcomeResultValueId.applicationSubmitted}`,
  [mortgageApplicationOutcomeResultValueId.applicationNotSubmitted]: `${mortgageApplicationOutcomeDefinitionId}:${mortgageApplicationOutcomeResultValueId.applicationNotSubmitted}`,
  [mortgageApplicationOutcomeResultValueId.unqualified]: `${mortgageApplicationOutcomeDefinitionId}:${mortgageApplicationOutcomeResultValueId.unqualified}`,
  [mortgageApplicationOutcomeResultValueId.notApplicable]: `${mortgageApplicationOutcomeDefinitionId}:${mortgageApplicationOutcomeResultValueId.notApplicable}`
};
const mortgageApplicationOutcomeResultLabel = {
  [mortgageApplicationOutcomeResultValueId.applicationSubmitted]: "Application Submitted",
  [mortgageApplicationOutcomeResultValueId.applicationNotSubmitted]: "Application Not Submitted",
  [mortgageApplicationOutcomeResultValueId.unqualified]: "Unqualified",
  [mortgageApplicationOutcomeResultValueId.notApplicable]: "N/A"
};
const mortgageApplicationOutcomeNoun = "application";
const mortgageRateLockOutcomeDefinitionId = "mortgage_rate_lock";
const mortgageRateLockOutcomeDefinitionName = "Mortgage Rate Lock";
const mortgageRateLockOutcomeResultValueId = {
  rateLocked: "rate_locked",
  rateNotLocked: "rate_not_locked",
  disqualified: "disqualified",
  notApplicable: "not_applicable"
};
const mortgageRateLockOutcomeResultValue = {
  [mortgageRateLockOutcomeResultValueId.rateLocked]: `${mortgageRateLockOutcomeDefinitionId}:${mortgageRateLockOutcomeResultValueId.rateLocked}`,
  [mortgageRateLockOutcomeResultValueId.rateNotLocked]: `${mortgageRateLockOutcomeDefinitionId}:${mortgageRateLockOutcomeResultValueId.rateNotLocked}`,
  [mortgageRateLockOutcomeResultValueId.disqualified]: `${mortgageRateLockOutcomeDefinitionId}:${mortgageRateLockOutcomeResultValueId.disqualified}`,
  [mortgageRateLockOutcomeResultValueId.notApplicable]: `${mortgageRateLockOutcomeDefinitionId}:${mortgageRateLockOutcomeResultValueId.notApplicable}`
};
const mortgageRateLockOutcomeResultLabel = {
  [mortgageRateLockOutcomeResultValueId.rateLocked]: "Rate Locked",
  [mortgageRateLockOutcomeResultValueId.rateNotLocked]: "Rate Not Locked",
  [mortgageRateLockOutcomeResultValueId.disqualified]: "Disqualified",
  [mortgageRateLockOutcomeResultValueId.notApplicable]: "N/A"
};
const mortgageRateLockOutcomeNoun = "rate lock";
const supportResolutionOutcomeDefinitionId = "support_resolution";
const supportResolutionOutcomeDefinitionName = "Support Resolution";
const supportResolutionOutcomeResultValueId = {
  resolved: "resolved",
  unresolved: "unresolved",
  escalated: "escalated",
  notApplicable: "not_applicable"
};
const supportResolutionOutcomeResultValue = {
  [supportResolutionOutcomeResultValueId.resolved]: `${supportResolutionOutcomeDefinitionId}:${supportResolutionOutcomeResultValueId.resolved}`,
  [supportResolutionOutcomeResultValueId.unresolved]: `${supportResolutionOutcomeDefinitionId}:${supportResolutionOutcomeResultValueId.unresolved}`,
  [supportResolutionOutcomeResultValueId.escalated]: `${supportResolutionOutcomeDefinitionId}:${supportResolutionOutcomeResultValueId.escalated}`,
  [supportResolutionOutcomeResultValueId.notApplicable]: `${supportResolutionOutcomeDefinitionId}:${supportResolutionOutcomeResultValueId.notApplicable}`
};
const supportResolutionOutcomeResultLabel = {
  [supportResolutionOutcomeResultValueId.resolved]: "Resolved",
  [supportResolutionOutcomeResultValueId.unresolved]: "Unresolved",
  [supportResolutionOutcomeResultValueId.escalated]: "Escalated",
  [supportResolutionOutcomeResultValueId.notApplicable]: "N/A"
};
const supportResolutionOutcomeNoun = "resolution";
const refinancingOutcomeDefinitionId = "refinancing";
const refinancingOutcomeDefinitionName = "Refinancing";
const refinancingOutcomeResultValueId = {
  appraisalOrdered: "appraisal_ordered",
  applicationSubmitted: "application_submitted",
  applicationInProgress: "application_in_progress",
  applicationWithdrawn: "application_withdrawn",
  applicationDenied: "application_denied",
  notApplicable: "not_applicable"
};
const refinancingOutcomeResultValue = {
  [refinancingOutcomeResultValueId.appraisalOrdered]: `${refinancingOutcomeDefinitionId}:${refinancingOutcomeResultValueId.appraisalOrdered}`,
  [refinancingOutcomeResultValueId.applicationSubmitted]: `${refinancingOutcomeDefinitionId}:${refinancingOutcomeResultValueId.applicationSubmitted}`,
  [refinancingOutcomeResultValueId.applicationInProgress]: `${refinancingOutcomeDefinitionId}:${refinancingOutcomeResultValueId.applicationInProgress}`,
  [refinancingOutcomeResultValueId.applicationWithdrawn]: `${refinancingOutcomeDefinitionId}:${refinancingOutcomeResultValueId.applicationWithdrawn}`,
  [refinancingOutcomeResultValueId.applicationDenied]: `${refinancingOutcomeDefinitionId}:${refinancingOutcomeResultValueId.applicationDenied}`,
  [refinancingOutcomeResultValueId.notApplicable]: `${refinancingOutcomeDefinitionId}:${refinancingOutcomeResultValueId.notApplicable}`
};
const refinancingLegacyValueMap = Object.freeze({
  [`${refinancingOutcomeDefinitionId}:application_submitted_or_in_progress`]: refinancingOutcomeResultValue[refinancingOutcomeResultValueId.applicationInProgress],
  [`${refinancingOutcomeDefinitionId}:application_withdrawn_or_denied`]: refinancingOutcomeResultValue[refinancingOutcomeResultValueId.applicationWithdrawn]
});
const refinancingOutcomeResultLabel = {
  [refinancingOutcomeResultValueId.appraisalOrdered]: "Appraisal Ordered",
  [refinancingOutcomeResultValueId.applicationSubmitted]: "Application Submitted",
  [refinancingOutcomeResultValueId.applicationInProgress]: "Application In Progress",
  [refinancingOutcomeResultValueId.applicationWithdrawn]: "Application Withdrawn",
  [refinancingOutcomeResultValueId.applicationDenied]: "Application Denied",
  [refinancingOutcomeResultValueId.notApplicable]: "N/A"
};
const refinancingOutcomeNoun = "AO";
const qualificationOutcomeDefinitionId = "qualification";
const qualificationOutcomeDefinitionName = "Qualification";
const qualificationOutcomeResultValueId = {
  qualification_completed: "qualification_completed",
  incomplete: "incomplete",
  notApplicable: "not_applicable"
};
const qualificationOutcomeResultValue = {
  [qualificationOutcomeResultValueId.qualification_completed]: `${qualificationOutcomeDefinitionId}:${qualificationOutcomeResultValueId.qualification_completed}`,
  [qualificationOutcomeResultValueId.incomplete]: `${qualificationOutcomeDefinitionId}:${qualificationOutcomeResultValueId.incomplete}`,
  [qualificationOutcomeResultValueId.notApplicable]: `${qualificationOutcomeDefinitionId}:${qualificationOutcomeResultValueId.notApplicable}`
};
const qualificationOutcomeResultLabel = {
  [qualificationOutcomeResultValueId.qualification_completed]: "Qualification completed",
  [qualificationOutcomeResultValueId.incomplete]: "Incomplete",
  [qualificationOutcomeResultValueId.notApplicable]: "N/A"
};
const qualificationOutcomeNoun = "qualification";
const revenueOutcomeDefinitionId = "revenue";
const revenueOutcomeDefinitionName = "Revenue";
const revenueOutcomeResultValueId = {
  won: "won",
  lost: "lost",
  pending: "pending"
};
const revenueOutcomeResultValue = {
  [revenueOutcomeResultValueId.won]: `${revenueOutcomeDefinitionId}:${revenueOutcomeResultValueId.won}`,
  [revenueOutcomeResultValueId.lost]: `${revenueOutcomeDefinitionId}:${revenueOutcomeResultValueId.lost}`,
  [revenueOutcomeResultValueId.pending]: `${revenueOutcomeDefinitionId}:${revenueOutcomeResultValueId.pending}`
};
const revenueOutcomeResultLabel = {
  [revenueOutcomeResultValueId.won]: "Won",
  [revenueOutcomeResultValueId.lost]: "Lost",
  [revenueOutcomeResultValueId.pending]: "Pending"
};
const revenueOutcomeNoun = "revenue";
const revenueOutcomeResults = [
  {
    category: greenRedYellowResultTypeCategory.green,
    value: revenueOutcomeResultValue[revenueOutcomeResultValueId.won],
    label: revenueOutcomeResultLabel[revenueOutcomeResultValueId.won]
  },
  {
    category: greenRedYellowResultTypeCategory.red,
    value: revenueOutcomeResultValue[revenueOutcomeResultValueId.lost],
    label: revenueOutcomeResultLabel[revenueOutcomeResultValueId.lost]
  },
  {
    category: greenRedYellowResultTypeCategory.gray,
    value: revenueOutcomeResultValue[revenueOutcomeResultValueId.pending],
    label: revenueOutcomeResultLabel[revenueOutcomeResultValueId.pending]
  }
];
function buildRevenueOutcomeDefinition(customFieldDefinitionId) {
  return {
    type: outcomeDefinitionType.preset,
    id: revenueOutcomeDefinitionId,
    name: revenueOutcomeDefinitionName,
    result_type: outcomeResultType.greenRedYellow,
    context: "",
    custom_field_definition_id: customFieldDefinitionId,
    results: [...revenueOutcomeResults]
  };
}
const revenueOutcomeDefinitionSchema = outcomeDefinitionBaseSchema.extend({
  type: z.literal(outcomeDefinitionType.preset),
  id: z.literal(revenueOutcomeDefinitionId),
  custom_field_definition_id: z.string().min(1)
});
const funnelOutcomeDefinitionId = "funnel";
const funnelOutcomeDefinitionName = "Funnel";
const funnelOutcomeStageSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  category: z.enum([
    greenRedYellowResultTypeCategory.green,
    greenRedYellowResultTypeCategory.red,
    greenRedYellowResultTypeCategory.yellow
  ]),
  // Raw CRM/custom-field values that should be bucketed into this stage.
  // Matching is normalized later so admins can paste values without worrying
  // about case or incidental whitespace.
  values: z.array(z.string()).default([])
});
const funnelOutcomeDefinitionSchema = outcomeDefinitionBaseSchema.extend({
  type: z.literal(outcomeDefinitionType.preset),
  id: z.literal(funnelOutcomeDefinitionId),
  custom_field_definition_id: z.string().min(1),
  stages: z.array(funnelOutcomeStageSchema).min(1)
}).superRefine((definition, ctx) => {
  const stageIds = /* @__PURE__ */ new Set();
  const mappedValues = /* @__PURE__ */ new Set();
  let greenStageCount = 0;
  for (const [index, stage] of definition.stages.entries()) {
    if (stageIds.has(stage.id)) {
      ctx.addIssue({
        code: "custom",
        path: ["stages", index, "id"],
        message: `Duplicate funnel stage id: ${stage.id}`
      });
    }
    stageIds.add(stage.id);
    if (stage.category === greenRedYellowResultTypeCategory.green) {
      greenStageCount += 1;
    }
    for (const value of stage.values) {
      const normalizedValue = normalizeFunnelValue(value);
      if (!normalizedValue) {
        continue;
      }
      if (mappedValues.has(normalizedValue)) {
        ctx.addIssue({
          code: "custom",
          path: ["stages", index, "values"],
          message: `Duplicate mapped funnel value: ${value}`
        });
      }
      mappedValues.add(normalizedValue);
    }
  }
  if (greenStageCount !== 1) {
    ctx.addIssue({
      code: "custom",
      path: ["stages"],
      message: "Funnel outcomes must have exactly one green stage."
    });
  }
});
function buildFunnelOutcomeDefinition({
  customFieldDefinitionId,
  stages
}) {
  return funnelOutcomeDefinitionSchema.parse({
    type: outcomeDefinitionType.preset,
    id: funnelOutcomeDefinitionId,
    name: funnelOutcomeDefinitionName,
    result_type: outcomeResultType.greenRedYellow,
    context: "",
    custom_field_definition_id: customFieldDefinitionId,
    stages,
    results: buildFunnelOutcomeResults(stages)
  });
}
const contactCustomFieldPresenceOutcomeDefinitionId = "contact_custom_field_presence";
const contactCustomFieldPresenceOutcomeDefinitionName = "Blank or filled";
const contactCustomFieldPresenceBucketId = {
  filled: "filled",
  blank: "blank"
};
const contactCustomFieldPresenceBucketSchema = z.object({
  category: z.enum([
    greenRedYellowResultTypeCategory.green,
    greenRedYellowResultTypeCategory.red,
    greenRedYellowResultTypeCategory.yellow
  ]),
  label: z.string().min(1)
});
const contactCustomFieldPresenceOutcomeDefinitionSchema = outcomeDefinitionBaseSchema.extend({
  type: z.literal(outcomeDefinitionType.preset),
  id: z.literal(contactCustomFieldPresenceOutcomeDefinitionId),
  custom_field_definition_id: z.string().min(1),
  filled: contactCustomFieldPresenceBucketSchema,
  blank: contactCustomFieldPresenceBucketSchema
}).superRefine((definition, ctx) => {
  const greenBuckets = [definition.filled, definition.blank].filter(
    (bucket) => bucket.category === greenRedYellowResultTypeCategory.green
  );
  if (greenBuckets.length !== 1) {
    ctx.addIssue({
      code: "custom",
      path: ["filled"],
      message: "Blank or filled outcomes must have exactly one green result."
    });
  }
});
function buildContactCustomFieldPresenceValue(bucketId) {
  return `${contactCustomFieldPresenceOutcomeDefinitionId}:${bucketId}`;
}
function buildContactCustomFieldPresenceResults(filled, blank) {
  return [
    {
      category: filled.category,
      value: buildContactCustomFieldPresenceValue(
        contactCustomFieldPresenceBucketId.filled
      ),
      label: filled.label
    },
    {
      category: blank.category,
      value: buildContactCustomFieldPresenceValue(
        contactCustomFieldPresenceBucketId.blank
      ),
      label: blank.label
    }
  ];
}
function buildContactCustomFieldPresenceOutcomeDefinition({
  customFieldDefinitionId,
  filled,
  blank
}) {
  return contactCustomFieldPresenceOutcomeDefinitionSchema.parse({
    type: outcomeDefinitionType.preset,
    id: contactCustomFieldPresenceOutcomeDefinitionId,
    name: contactCustomFieldPresenceOutcomeDefinitionName,
    result_type: outcomeResultType.greenRedYellow,
    context: "",
    custom_field_definition_id: customFieldDefinitionId,
    filled,
    blank,
    results: buildContactCustomFieldPresenceResults(filled, blank)
  });
}
const reverseMortgageOutcomeDefinitionId = "reverse_mortgage";
const reverseMortgageOutcomeDefinitionName = "Reverse Mortgage";
const reverseMortgageOutcomeResultValueId = {
  counselingScheduled: "counseling_scheduled",
  counselingNotScheduled: "counseling_not_scheduled",
  unqualified: "unqualified",
  notApplicable: "not_applicable"
};
const reverseMortgageOutcomeResultValue = {
  [reverseMortgageOutcomeResultValueId.counselingScheduled]: `${reverseMortgageOutcomeDefinitionId}:${reverseMortgageOutcomeResultValueId.counselingScheduled}`,
  [reverseMortgageOutcomeResultValueId.counselingNotScheduled]: `${reverseMortgageOutcomeDefinitionId}:${reverseMortgageOutcomeResultValueId.counselingNotScheduled}`,
  [reverseMortgageOutcomeResultValueId.unqualified]: `${reverseMortgageOutcomeDefinitionId}:${reverseMortgageOutcomeResultValueId.unqualified}`,
  [reverseMortgageOutcomeResultValueId.notApplicable]: `${reverseMortgageOutcomeDefinitionId}:${reverseMortgageOutcomeResultValueId.notApplicable}`
};
const reverseMortgageOutcomeResultLabel = {
  [reverseMortgageOutcomeResultValueId.counselingScheduled]: "Counseling Scheduled",
  [reverseMortgageOutcomeResultValueId.counselingNotScheduled]: "Counseling Not Scheduled",
  [reverseMortgageOutcomeResultValueId.unqualified]: "Unqualified",
  [reverseMortgageOutcomeResultValueId.notApplicable]: "N/A"
};
const reverseMortgageOutcomeNoun = "counseling";
const simplePresetOutcomeDefinitionIdSchema = z.enum([
  winLossOutcomeDefinitionId,
  insuranceEnrollmentOutcomeDefinitionId,
  insuranceApplicationOutcomeDefinitionId,
  mortgageApplicationOutcomeDefinitionId,
  mortgageRateLockOutcomeDefinitionId,
  supportResolutionOutcomeDefinitionId,
  refinancingOutcomeDefinitionId,
  qualificationOutcomeDefinitionId,
  reverseMortgageOutcomeDefinitionId
]);
const simplePresetOutcomeDefinitionSchema = outcomeDefinitionBaseSchema.extend({
  type: z.literal(outcomeDefinitionType.preset),
  id: simplePresetOutcomeDefinitionIdSchema
});
const customOutcomeDefinitionSchema = outcomeDefinitionBaseSchema.extend({
  type: z.literal(outcomeDefinitionType.custom),
  id: z.uuid()
});
const outcomeDefinitionSchema = z.union([
  funnelOutcomeDefinitionSchema,
  contactCustomFieldPresenceOutcomeDefinitionSchema,
  revenueOutcomeDefinitionSchema,
  simplePresetOutcomeDefinitionSchema,
  customOutcomeDefinitionSchema
]);
function isFunnelOutcomeDefinition(outcomeDefinition) {
  return funnelOutcomeDefinitionSchema.safeParse(outcomeDefinition).success;
}
function isContactCustomFieldPresenceOutcomeDefinition(outcomeDefinition) {
  return contactCustomFieldPresenceOutcomeDefinitionSchema.safeParse(
    outcomeDefinition
  ).success;
}
function getOutcomeDisplayName(outcomeDefinition) {
  if (!outcomeDefinition || isRevenueOutcomeDefinition(outcomeDefinition) || isFunnelOutcomeDefinition(outcomeDefinition) || isContactCustomFieldPresenceOutcomeDefinition(outcomeDefinition)) {
    return "Outcome";
  }
  return outcomeDefinition.name;
}
function buildFunnelOutcomeValue(stageId) {
  return `${funnelOutcomeDefinitionId}:${stageId}`;
}
const funnelOutcomeNotApplicableValue = buildFunnelOutcomeValue("not_applicable");
function getFunnelStageForRawValue(outcomeDefinition, rawValue) {
  const normalizedRawValue = normalizeFunnelValue(rawValue);
  if (!normalizedRawValue) {
    return void 0;
  }
  return outcomeDefinition.stages.find(
    (stage) => stage.values.some(
      (mappedValue) => normalizeFunnelValue(mappedValue) === normalizedRawValue
    )
  );
}
function normalizeFunnelValue(value) {
  switch (typeof value) {
    case "string":
    case "number":
    case "boolean":
    case "bigint":
      return value.toString().trim().toLowerCase();
    default:
      return "";
  }
}
function buildFunnelOutcomeResults(stages) {
  return [
    ...stages.map((stage) => ({
      category: stage.category,
      value: buildFunnelOutcomeValue(stage.id),
      label: stage.name
    })),
    {
      category: greenRedYellowResultTypeCategory.gray,
      value: funnelOutcomeNotApplicableValue,
      label: "N/A"
    }
  ];
}
const presetOutcomeNouns = {
  [winLossOutcomeDefinitionId]: winLossOutcomeNoun,
  [insuranceEnrollmentOutcomeDefinitionId]: insuranceEnrollmentOutcomeNoun,
  [insuranceApplicationOutcomeDefinitionId]: insuranceApplicationOutcomeNoun,
  [mortgageApplicationOutcomeDefinitionId]: mortgageApplicationOutcomeNoun,
  [mortgageRateLockOutcomeDefinitionId]: mortgageRateLockOutcomeNoun,
  [supportResolutionOutcomeDefinitionId]: supportResolutionOutcomeNoun,
  [refinancingOutcomeDefinitionId]: refinancingOutcomeNoun,
  [qualificationOutcomeDefinitionId]: qualificationOutcomeNoun,
  [revenueOutcomeDefinitionId]: revenueOutcomeNoun,
  [reverseMortgageOutcomeDefinitionId]: reverseMortgageOutcomeNoun
};
function getOutcomeNoun(outcomeDefinition) {
  const override = outcomeDefinition.singular_noun?.trim();
  if (override) {
    return override;
  }
  if (isRevenueOutcomeDefinition(outcomeDefinition)) {
    return "Inforce policy";
  }
  const presetNoun = presetOutcomeNouns[outcomeDefinition.id];
  if (presetNoun) {
    return presetNoun;
  }
  const greenResult = outcomeDefinition.results.find(
    (r) => r.category === greenRedYellowResultTypeCategory.green
  );
  const normalizedGreenLabel = (greenResult?.label ?? outcomeDefinition.name).toLowerCase();
  return normalizedGreenLabel === "won" ? "win" : normalizedGreenLabel;
}
function pluralizeOutcomeNoun(outcomeNoun, outcomePluralNoun, count) {
  const normalizedCount = count !== void 0 ? Math.round(count) : void 0;
  if (normalizedCount === 1) {
    return outcomeNoun;
  }
  const normalizedPluralNoun = outcomePluralNoun?.trim();
  if (normalizedPluralNoun) {
    return normalizedPluralNoun;
  }
  return pluralize(outcomeNoun, normalizedCount);
}
function getOutcomePluralNoun(outcomeDefinition, count) {
  return pluralizeOutcomeNoun(
    getOutcomeNoun(outcomeDefinition),
    outcomeDefinition.plural_noun,
    count
  );
}
function buildOutcomeGreenRateLabel(outcomeDefinition) {
  const override = outcomeDefinition.rate_label?.trim();
  if (override) {
    return override;
  }
  if (isRevenueOutcomeDefinition(outcomeDefinition)) {
    return "Inforce rate";
  }
  const noun = getOutcomeNoun(outcomeDefinition);
  return `${noun.charAt(0).toUpperCase() + noun.slice(1)} rate`;
}
function buildOutcomeGreenResultLabel(outcomeDefinition) {
  return buildOutcomeResultLabel(
    outcomeDefinition,
    greenRedYellowResultTypeCategory.green
  ) ?? outcomeDefinition.name;
}
function buildOutcomeResultLabel(outcomeDefinition, category) {
  const result = outcomeDefinition.results.find((r) => r.category === category);
  return result?.label;
}
const incompleteOutcomeTerminologyReason = "Customize the outcome positive result labels before publishing";
function hasCompleteOutcomeDefinition(outcomeDefinition) {
  if (isFunnelOutcomeDefinition(outcomeDefinition) || isContactCustomFieldPresenceOutcomeDefinition(outcomeDefinition)) {
    return [
      outcomeDefinition.singular_noun,
      outcomeDefinition.plural_noun,
      outcomeDefinition.rate_label
    ].every((label) => (label?.trim().length ?? 0) > 0);
  }
  return true;
}
const defaultOutcomeDefinitions = Object.freeze([
  {
    type: outcomeDefinitionType.preset,
    id: winLossOutcomeDefinitionId,
    name: winLossOutcomeDefinitionLabel,
    result_type: outcomeResultType.greenRedYellow,
    context: `Set the outcome to "${winLossOutcomeResultLabel[winLossOutcomeResultValueId.won]}" when the prospect clearly agrees to move forward with the product or service being sold and there is a concrete commitment, not just vague interest. The transcript should show the prospect explicitly accepting the offer, choosing a plan or product, or otherwise agreeing to become a customer. If the transcript makes it clear that the customer has committed and the rep is treating the deal as closed, choose "${winLossOutcomeResultLabel[winLossOutcomeResultValueId.won]}", even if some administrative steps will occur after the call.

Set the outcome to "${winLossOutcomeResultLabel[winLossOutcomeResultValueId.lost]}" when the prospect clearly decides not to move forward with any purchase or engagement at this time, despite being a legitimate potential customer. The transcript should show that the rep has presented an offer or recommendation and the prospect declines or shuts down the opportunity.

Set the outcome to "${winLossOutcomeResultLabel[winLossOutcomeResultValueId.unqualified]}" when it becomes clear during the call that the prospect does not meet the basic eligibility or fit criteria for becoming a customer for any of the products or services the rep is expected to sell. This includes regulatory or policy constraints (for example not in a licensed state, not in the eligible age or program range, required documentation cannot be provided, or company policy prohibits proceeding), or clear misfit with the business model (for example the caller needs a product that the company does not offer and the rep cannot reasonably propose an alternative). The transcript should show the rep explaining that they cannot move forward or that the company cannot serve the prospect's situation, and the call ending without any viable path to a sale. If the primary reason the opportunity does not proceed is lack of eligibility or fit, rather than customer preference, mark "${winLossOutcomeResultLabel[winLossOutcomeResultValueId.unqualified]}".

Do not set "${winLossOutcomeResultLabel[winLossOutcomeResultValueId.won]}", "${winLossOutcomeResultLabel[winLossOutcomeResultValueId.lost]}", or "${winLossOutcomeResultLabel[winLossOutcomeResultValueId.unqualified]}" and instead treat the outcome as "${winLossOutcomeResultLabel[winLossOutcomeResultValueId.notApplicable]}" (not applicable) if the transcripts have not reached the stage of the buying process where it is possible to evaluate an outcome, or the calls are unrelated to a sale or conversion event. This includes situations where we only have transcripts of calls from early in the sales process and there are future calls to be done where a decision will be made or situations where the transcripts are not related to a sales process (e.g. support or service calls).

`,
    results: [
      {
        category: greenRedYellowResultTypeCategory.green,
        value: winLossOutcomeResultValue[winLossOutcomeResultValueId.won],
        label: winLossOutcomeResultLabel[winLossOutcomeResultValueId.won]
      },
      {
        category: greenRedYellowResultTypeCategory.red,
        value: winLossOutcomeResultValue[winLossOutcomeResultValueId.lost],
        label: winLossOutcomeResultLabel[winLossOutcomeResultValueId.lost]
      },
      {
        category: greenRedYellowResultTypeCategory.yellow,
        value: winLossOutcomeResultValue[winLossOutcomeResultValueId.unqualified],
        label: winLossOutcomeResultLabel[winLossOutcomeResultValueId.unqualified]
      },
      {
        category: greenRedYellowResultTypeCategory.gray,
        value: winLossOutcomeResultValue[winLossOutcomeResultValueId.notApplicable],
        label: winLossOutcomeResultLabel[winLossOutcomeResultValueId.notApplicable]
      }
    ]
  },
  {
    type: outcomeDefinitionType.preset,
    id: insuranceEnrollmentOutcomeDefinitionId,
    name: insuranceEnrollmentOutcomeDefinitionName,
    result_type: outcomeResultType.greenRedYellow,
    context: `Set the outcome to "${insuranceEnrollmentOutcomeResultLabel[insuranceEnrollmentOutcomeResultValueId.enrolled]}" when the caller clearly ends the interaction enrolled in a new policy/plan, renewed coverage, or a plan change with the company. The transcript should show the caller selecting a specific plan or policy, agreeing to proceed, and the rep confirming that enrollment or an application for coverage is complete or being submitted now. Changing from one plan to another with the same company, adding coverage, or renewing coverage all count as "${insuranceEnrollmentOutcomeResultLabel[insuranceEnrollmentOutcomeResultValueId.enrolled]}" as long as the caller explicitly agrees and the rep treats the enrollment decision as final. If the customer clearly commits to the enrollment on the call, set to "${insuranceEnrollmentOutcomeResultLabel[insuranceEnrollmentOutcomeResultValueId.enrolled]}" even if some administrative steps will occur after the call.

Set the outcome to "${insuranceEnrollmentOutcomeResultLabel[insuranceEnrollmentOutcomeResultValueId.didNotEnroll]}" when the caller appears to be eligible for coverage, but no enrollment, renewal, or plan change is completed by the end of the call. The transcript should show that the rep has discussed options or coverage and the caller either declines, decides to stay with another provider, or chooses to \u201Cthink about it\u201D without making a clear commitment to enroll. Also use "${insuranceEnrollmentOutcomeResultLabel[insuranceEnrollmentOutcomeResultValueId.didNotEnroll]}" when the rep explains options but the call ends with the caller still uncommitted and no enrollment steps started or completed through the company.

Set the outcome to "${insuranceEnrollmentOutcomeResultLabel[insuranceEnrollmentOutcomeResultValueId.unqualified]}" when it becomes clear that the caller cannot be enrolled into any suitable policy or plan because of eligibility, licensing, or product constraints. Examples include being outside the eligible age range or geography, not meeting program requirements, or seeking a product the company cannot legally sell. The transcript should show the rep explaining that they cannot move forward with enrollment under current rules and that there is no appropriate product the company can offer. When the main reason for not enrolling is these constraints, not the caller's preference, choose Unqualified.

Do not set "${insuranceEnrollmentOutcomeResultLabel[insuranceEnrollmentOutcomeResultValueId.enrolled]}", "${insuranceEnrollmentOutcomeResultLabel[insuranceEnrollmentOutcomeResultValueId.didNotEnroll]}", or "${insuranceEnrollmentOutcomeResultLabel[insuranceEnrollmentOutcomeResultValueId.unqualified]}" and instead treat the outcome as "${insuranceEnrollmentOutcomeResultLabel[insuranceEnrollmentOutcomeResultValueId.notApplicable]}" (not applicable) if the transcripts have not reached the stage of discussing or deciding on an enrollment, or the calls are unrelated to enrollment decisions. This includes calls focused on support, billing, or claims for existing coverage without any discussion of changing or adding coverage, early discovery calls where the rep is only gathering information and promises to research options, very short or incomplete transcripts where it is unclear whether enrollment was discussed, or scheduling-only calls where the actual enrollment conversation will happen later.

`,
    results: [
      {
        category: greenRedYellowResultTypeCategory.green,
        value: insuranceEnrollmentOutcomeResultValue[insuranceEnrollmentOutcomeResultValueId.enrolled],
        label: insuranceEnrollmentOutcomeResultLabel[insuranceEnrollmentOutcomeResultValueId.enrolled]
      },
      {
        category: greenRedYellowResultTypeCategory.red,
        value: insuranceEnrollmentOutcomeResultValue[insuranceEnrollmentOutcomeResultValueId.didNotEnroll],
        label: insuranceEnrollmentOutcomeResultLabel[insuranceEnrollmentOutcomeResultValueId.didNotEnroll]
      },
      {
        category: greenRedYellowResultTypeCategory.yellow,
        value: insuranceEnrollmentOutcomeResultValue[insuranceEnrollmentOutcomeResultValueId.unqualified],
        label: insuranceEnrollmentOutcomeResultLabel[insuranceEnrollmentOutcomeResultValueId.unqualified]
      },
      {
        category: greenRedYellowResultTypeCategory.gray,
        value: insuranceEnrollmentOutcomeResultValue[insuranceEnrollmentOutcomeResultValueId.notApplicable],
        label: insuranceEnrollmentOutcomeResultLabel[insuranceEnrollmentOutcomeResultValueId.notApplicable]
      }
    ]
  },
  {
    type: outcomeDefinitionType.preset,
    id: insuranceApplicationOutcomeDefinitionId,
    name: insuranceApplicationOutcomeDefinitionName,
    result_type: outcomeResultType.greenRedYellow,
    context: `Set the outcome to "${insuranceApplicationOutcomeResultLabel[insuranceApplicationOutcomeResultValueId.applied]}" when the customer clearly agrees to move forward with a new insurance policy application and the call advances into a real application path. This includes things like a successful live transfer to an enrollment specialist, completing application steps on the call, scheduling or requesting a required exam with clear customer agreement, or clearly initiating a carrier-required link or interview flow with the customer's explicit commitment to proceed now. The key is that the call has moved beyond quote review into an active commitment-bearing next step. Do not require final underwriting approval, issued policy status, or first draft confirmation if the transcript already shows the application process has begun with clear customer commitment.

Set the outcome to "${insuranceApplicationOutcomeResultLabel[insuranceApplicationOutcomeResultValueId.didNotApply]}" when the customer hears a quote, recommendation, or viable path forward but does not clearly commit to starting the application path. Use this when the call ends with the customer wanting to think about it, review an email later, discuss with family, call back another time, or otherwise remain uncommitted. Also use this when the agent could have advanced the case further but the conversation ends without a meaningful application step being accepted.

Set the outcome to "${insuranceApplicationOutcomeResultLabel[insuranceApplicationOutcomeResultValueId.uninsurable]}" when the main reason the customer does not move forward is that the insurance product offered is not reasonably available because of issues like medical history, recent treatment or surgery, active cancer, severe health readings, criminal history, active bankruptcy, financial-insurability limits, overinsurance, immigration or residency restrictions, lack of SSN when required, product mismatch, licensing mismatch, or other stated eligibility barriers.

Set the outcome to "${insuranceApplicationOutcomeResultLabel[insuranceApplicationOutcomeResultValueId.transferred]}" if the agent offers or completes a transfer to a different department that can offer a different product, because the original insurance path sold by this team was not viable. Use this outcome when the transcript clearly shows that the customer is not a workable fit for the path sold by this team but could be a fit in another department.

Do not set "${insuranceApplicationOutcomeResultLabel[insuranceApplicationOutcomeResultValueId.applied]}", "${insuranceApplicationOutcomeResultLabel[insuranceApplicationOutcomeResultValueId.didNotApply]}", "${insuranceApplicationOutcomeResultLabel[insuranceApplicationOutcomeResultValueId.uninsurable]}", or "${insuranceApplicationOutcomeResultLabel[insuranceApplicationOutcomeResultValueId.transferred]}" and instead treat the outcome as "${insuranceApplicationOutcomeResultLabel[insuranceApplicationOutcomeResultValueId.notApplicable]}" (not applicable) when the call is primarily administrative, policy service only, beneficiary or billing support, an early information-gathering conversation with no quote or recommendation, or the transcript is too incomplete to tell whether the call reached a real sales decision point. Warm transfers from policyholder services should still be scored if the conversation turns into a real quote or coverage decision. If it stays informational or administrative, use "${insuranceApplicationOutcomeResultLabel[insuranceApplicationOutcomeResultValueId.notApplicable]}".

`,
    results: [
      {
        category: greenRedYellowResultTypeCategory.green,
        value: insuranceApplicationOutcomeResultValue[insuranceApplicationOutcomeResultValueId.applied],
        label: insuranceApplicationOutcomeResultLabel[insuranceApplicationOutcomeResultValueId.applied]
      },
      {
        category: greenRedYellowResultTypeCategory.red,
        value: insuranceApplicationOutcomeResultValue[insuranceApplicationOutcomeResultValueId.didNotApply],
        label: insuranceApplicationOutcomeResultLabel[insuranceApplicationOutcomeResultValueId.didNotApply]
      },
      {
        category: greenRedYellowResultTypeCategory.yellow,
        value: insuranceApplicationOutcomeResultValue[insuranceApplicationOutcomeResultValueId.uninsurable],
        label: insuranceApplicationOutcomeResultLabel[insuranceApplicationOutcomeResultValueId.uninsurable]
      },
      {
        category: greenRedYellowResultTypeCategory.yellow,
        value: insuranceApplicationOutcomeResultValue[insuranceApplicationOutcomeResultValueId.transferred],
        label: insuranceApplicationOutcomeResultLabel[insuranceApplicationOutcomeResultValueId.transferred]
      },
      {
        category: greenRedYellowResultTypeCategory.gray,
        value: insuranceApplicationOutcomeResultValue[insuranceApplicationOutcomeResultValueId.notApplicable],
        label: insuranceApplicationOutcomeResultLabel[insuranceApplicationOutcomeResultValueId.notApplicable]
      }
    ]
  },
  {
    type: outcomeDefinitionType.preset,
    id: mortgageApplicationOutcomeDefinitionId,
    name: mortgageApplicationOutcomeDefinitionName,
    result_type: outcomeResultType.greenRedYellow,
    context: `Set the outcome to "${mortgageApplicationOutcomeResultLabel[mortgageApplicationOutcomeResultValueId.applicationSubmitted]}" when the transcript clearly shows that a mortgage application has been completed and submitted during or immediately as a result of the call. The transcript should include the loan officer collecting detailed borrower information (such as income, assets, property details, and loan purpose) and confirming that an application has been created, submitted, or is now in the system for review. If the conversation ends with the borrower clearly authorizing the application and the loan officer treating it as submitted, choose "${mortgageApplicationOutcomeResultLabel[mortgageApplicationOutcomeResultValueId.applicationSubmitted]}", even if additional documents will be uploaded later.

Set the outcome to "${mortgageApplicationOutcomeResultLabel[mortgageApplicationOutcomeResultValueId.applicationNotSubmitted]}" when the caller is a plausible mortgage prospect but no application is actually submitted by the end of the call. The transcript may show rate and scenario discussions, high-level pre-qualification, or partial information gathering, but the borrower does not clearly authorize submission of an application. This includes cases where the borrower decides to wait, shop around, \u201Cthink about it,\u201D or receive more information first, as well as calls where the loan officer starts to collect data but the call ends before they confirm that an application has been submitted.

Set the outcome to "${mortgageApplicationOutcomeResultLabel[mortgageApplicationOutcomeResultValueId.unqualified]}" when it becomes clear that the borrower cannot proceed with a mortgage application because they do not meet basic qualifying criteria for any suitable loan product within the company's scope. The transcript should show the loan officer explaining that, based on income, credit, debt, property, program rules, or other key factors, there is no loan program that can be offered or that the borrower cannot qualify at this time. When the main reason no application is submitted is this lack of qualification, not the borrower's choice to delay or decline, choose "${mortgageApplicationOutcomeResultLabel[mortgageApplicationOutcomeResultValueId.unqualified]}".

Do not set "${mortgageApplicationOutcomeResultLabel[mortgageApplicationOutcomeResultValueId.applicationSubmitted]}", "${mortgageApplicationOutcomeResultLabel[mortgageApplicationOutcomeResultValueId.applicationNotSubmitted]}", or "${mortgageApplicationOutcomeResultLabel[mortgageApplicationOutcomeResultValueId.unqualified]}" and instead treat the outcome as "${mortgageApplicationOutcomeResultLabel[mortgageApplicationOutcomeResultValueId.notApplicable]}" (not applicable) if the transcripts are too early in the buyer evaluation process, do not meaningfully concern starting or advancing a new mortgage application, or if there is not enough information to determine the application status. This includes calls that only check the status of an existing application or loan, calls about post-closing or servicing issues, very short or incomplete transcripts where the purpose of the call is unclear, or early exploratory calls where the loan officer shares general information and the borrower's intent to apply is not yet discussed or decided.

`,
    results: [
      {
        category: greenRedYellowResultTypeCategory.green,
        value: mortgageApplicationOutcomeResultValue[mortgageApplicationOutcomeResultValueId.applicationSubmitted],
        label: mortgageApplicationOutcomeResultLabel[mortgageApplicationOutcomeResultValueId.applicationSubmitted]
      },
      {
        category: greenRedYellowResultTypeCategory.red,
        value: mortgageApplicationOutcomeResultValue[mortgageApplicationOutcomeResultValueId.applicationNotSubmitted],
        label: mortgageApplicationOutcomeResultLabel[mortgageApplicationOutcomeResultValueId.applicationNotSubmitted]
      },
      {
        category: greenRedYellowResultTypeCategory.yellow,
        value: mortgageApplicationOutcomeResultValue[mortgageApplicationOutcomeResultValueId.unqualified],
        label: mortgageApplicationOutcomeResultLabel[mortgageApplicationOutcomeResultValueId.unqualified]
      },
      {
        category: greenRedYellowResultTypeCategory.gray,
        value: mortgageApplicationOutcomeResultValue[mortgageApplicationOutcomeResultValueId.notApplicable],
        label: mortgageApplicationOutcomeResultLabel[mortgageApplicationOutcomeResultValueId.notApplicable]
      }
    ]
  },
  {
    type: outcomeDefinitionType.preset,
    id: supportResolutionOutcomeDefinitionId,
    name: supportResolutionOutcomeDefinitionName,
    result_type: outcomeResultType.greenRedYellow,
    context: `Set the outcome to "${supportResolutionOutcomeResultLabel[supportResolutionOutcomeResultValueId.resolved]}" when the customer's primary issue is clearly addressed by the end of the call and there is no open question about what will happen next. The transcript should show that the agent has either fixed the problem directly (for example corrected an error, updated information, explained coverage or a bill in a way the customer accepts) or completed a concrete action that solves it (such as ordering a new card or confirming a change). The customer typically acknowledges that their question or problem is handled and does not indicate any remaining concern about the main issue.

Set the outcome to "${supportResolutionOutcomeResultLabel[supportResolutionOutcomeResultValueId.escalated]}" when the agent cannot fully resolve the primary issue during the call but creates a clear, structured handoff or follow-up with another person or team. The transcript should show the agent opening a case, transferring the call to a specialist or supervisor, scheduling a callback, or contacting a carrier or internal team on the customer's behalf, and explaining what will happen next. If the issue is still open but has been appropriately passed to the next step with defined follow-up, choose "${supportResolutionOutcomeResultLabel[supportResolutionOutcomeResultValueId.escalated]}".

Set the outcome to "${supportResolutionOutcomeResultLabel[supportResolutionOutcomeResultValueId.unresolved]}" when the primary issue remains open at the end of the call and there is no clear resolution or structured escalation. The transcript may show the customer still confused, dissatisfied, or lacking an answer, or the call may end abruptly (for example the customer hangs up) while the problem is still unresolved. Also choose "${supportResolutionOutcomeResultLabel[supportResolutionOutcomeResultValueId.unresolved]}" when the agent indicates that nothing further can be done and does not provide a solution or meaningful next step.

Do not set "${supportResolutionOutcomeResultLabel[supportResolutionOutcomeResultValueId.resolved]}", "${supportResolutionOutcomeResultLabel[supportResolutionOutcomeResultValueId.escalated]}", or "${supportResolutionOutcomeResultLabel[supportResolutionOutcomeResultValueId.unresolved]}" and instead treat the outcome as "${supportResolutionOutcomeResultLabel[supportResolutionOutcomeResultValueId.notApplicable]}" (not applicable) if the transcripts do not involve a specific support or service issue, or if there is not enough information to identify the issue and its status. This includes calls that are purely sales-focused, very short or partial transcripts where the customer's problem is not clearly described, simple routing or message-taking calls with no attempt to handle the issue, or situations where the conversation is cut off before it is possible to tell whether the main issue was resolved, escalated, or left unresolved.

`,
    results: [
      {
        category: greenRedYellowResultTypeCategory.green,
        value: supportResolutionOutcomeResultValue[supportResolutionOutcomeResultValueId.resolved],
        label: supportResolutionOutcomeResultLabel[supportResolutionOutcomeResultValueId.resolved]
      },
      {
        category: greenRedYellowResultTypeCategory.red,
        value: supportResolutionOutcomeResultValue[supportResolutionOutcomeResultValueId.unresolved],
        label: supportResolutionOutcomeResultLabel[supportResolutionOutcomeResultValueId.unresolved]
      },
      {
        category: greenRedYellowResultTypeCategory.yellow,
        value: supportResolutionOutcomeResultValue[supportResolutionOutcomeResultValueId.escalated],
        label: supportResolutionOutcomeResultLabel[supportResolutionOutcomeResultValueId.escalated]
      },
      {
        category: greenRedYellowResultTypeCategory.gray,
        value: supportResolutionOutcomeResultValue[supportResolutionOutcomeResultValueId.notApplicable],
        label: supportResolutionOutcomeResultLabel[supportResolutionOutcomeResultValueId.notApplicable]
      }
    ]
  },
  {
    type: outcomeDefinitionType.preset,
    id: refinancingOutcomeDefinitionId,
    name: refinancingOutcomeDefinitionName,
    result_type: outcomeResultType.greenRedYellow,
    context: `Set the outcome to "${refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.appraisalOrdered]}" when the refinancing application has proceeded through the application process and the appraisal of the home has been initiated or completed. The transcript should show that the borrower has submitted all required documentation, the application has been processed, and the lender has ordered or completed the home appraisal. This indicates the end of the sales-led portion of the refinancing process and the beginning of the underwriting phase.

Set the outcome to "${refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.applicationSubmitted]}" when a refinancing application has been formally submitted to the lender but is not yet being actively processed. The transcript should show that the borrower has completed and submitted the application paperwork, but the lender has not yet begun reviewing or processing it.

Set the outcome to "${refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.applicationInProgress]}" when a refinancing application is actively being processed but the appraisal stage has not yet been reached. The transcript should show that the borrower has started the application process, provided initial documentation, or is working with the loan officer to complete the application, but the appraisal has not yet been ordered or initiated.

Set the outcome to "${refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.applicationWithdrawn]}" when the refinancing application has been withdrawn by the borrower. The transcript should show that the borrower has decided not to proceed with the refinancing or has cancelled their application.

Set the outcome to "${refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.applicationDenied]}" when the refinancing application has been denied by the lender. The transcript should show that the lender has determined the borrower does not qualify for the refinancing product.

Do not set "${refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.appraisalOrdered]}", "${refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.applicationSubmitted]}", "${refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.applicationInProgress]}", "${refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.applicationWithdrawn]}", or "${refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.applicationDenied]}" and instead treat the outcome as "${refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.notApplicable]}" (not applicable) if the transcripts are not related to a refinancing process, if the call is purely exploratory without any application activity, or if there is insufficient information to determine the status of a refinancing application. This includes calls about new home purchases, general rate inquiries without refinancing intent, or very short or unclear transcripts where the purpose of the call cannot be determined.

`,
    results: [
      {
        category: greenRedYellowResultTypeCategory.green,
        value: refinancingOutcomeResultValue[refinancingOutcomeResultValueId.appraisalOrdered],
        label: refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.appraisalOrdered]
      },
      {
        category: greenRedYellowResultTypeCategory.red,
        value: refinancingOutcomeResultValue[refinancingOutcomeResultValueId.applicationWithdrawn],
        label: refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.applicationWithdrawn]
      },
      {
        category: greenRedYellowResultTypeCategory.red,
        value: refinancingOutcomeResultValue[refinancingOutcomeResultValueId.applicationDenied],
        label: refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.applicationDenied]
      },
      {
        category: greenRedYellowResultTypeCategory.yellow,
        value: refinancingOutcomeResultValue[refinancingOutcomeResultValueId.applicationSubmitted],
        label: refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.applicationSubmitted]
      },
      {
        category: greenRedYellowResultTypeCategory.yellow,
        value: refinancingOutcomeResultValue[refinancingOutcomeResultValueId.applicationInProgress],
        label: refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.applicationInProgress]
      },
      {
        category: greenRedYellowResultTypeCategory.gray,
        value: refinancingOutcomeResultValue[refinancingOutcomeResultValueId.notApplicable],
        label: refinancingOutcomeResultLabel[refinancingOutcomeResultValueId.notApplicable]
      }
    ]
  },
  {
    type: outcomeDefinitionType.preset,
    id: qualificationOutcomeDefinitionId,
    name: qualificationOutcomeDefinitionName,
    result_type: outcomeResultType.greenRedYellow,
    context: `This outcome measures whether the qualification process was performed correctly during the call. The agent or teammate is responsible for initial qualification of potentially interested buyers.

Set the outcome to "${qualificationOutcomeResultLabel[qualificationOutcomeResultValueId.qualification_completed]}" when the qualification process was fully and correctly performed. The transcript should show that the agent asked all necessary qualifying questions, gathered the required information to determine whether the lead meets the criteria, and reached a clear determination about the lead's qualification status. The lead itself may be either qualified or unqualified for the service\u2014what matters is that the qualification process was completed correctly and thoroughly.

Set the outcome to "${qualificationOutcomeResultLabel[qualificationOutcomeResultValueId.incomplete]}" when the qualification process was not fully completed. This includes cases of early dropoff where the call ended before the qualification could be finished, situations where the prospect hung up or became unreachable or frustrated, wrong numbers or disconnected calls, or calls where for any other reason the qualification process was not fully performed.

Do not set "${qualificationOutcomeResultLabel[qualificationOutcomeResultValueId.qualification_completed]}" or "${qualificationOutcomeResultLabel[qualificationOutcomeResultValueId.incomplete]}" and instead treat the outcome as "${qualificationOutcomeResultLabel[qualificationOutcomeResultValueId.notApplicable]}" (not applicable) if the call was not intended to be a qualification call, if the transcript does not involve lead qualification, or if there is insufficient information to determine whether qualification was attempted. This includes calls that are purely support or service-related, follow-up calls where qualification was already completed previously, or very short or unclear transcripts where the purpose of the call cannot be determined.
`,
    results: [
      {
        category: greenRedYellowResultTypeCategory.green,
        value: qualificationOutcomeResultValue[qualificationOutcomeResultValueId.qualification_completed],
        label: qualificationOutcomeResultLabel[qualificationOutcomeResultValueId.qualification_completed]
      },
      {
        category: greenRedYellowResultTypeCategory.red,
        value: qualificationOutcomeResultValue[qualificationOutcomeResultValueId.incomplete],
        label: qualificationOutcomeResultLabel[qualificationOutcomeResultValueId.incomplete]
      },
      {
        category: greenRedYellowResultTypeCategory.gray,
        value: qualificationOutcomeResultValue[qualificationOutcomeResultValueId.notApplicable],
        label: qualificationOutcomeResultLabel[qualificationOutcomeResultValueId.notApplicable]
      }
    ]
  },
  {
    type: outcomeDefinitionType.preset,
    id: mortgageRateLockOutcomeDefinitionId,
    name: mortgageRateLockOutcomeDefinitionName,
    result_type: outcomeResultType.greenRedYellow,
    context: `Set the outcome to "${mortgageRateLockOutcomeResultLabel[mortgageRateLockOutcomeResultValueId.rateLocked]}" when the transcripts clearly show that the borrower locked an interest rate on a specific property. The transcripts should show the loan officer confirming a lock has been executed, discussing the locked rate and its terms, or walking the borrower through post-lock next steps such as document submission and closing timeline. If the borrower has an accepted offer on a property and the loan officer confirms or references the rate as locked, choose "${mortgageRateLockOutcomeResultLabel[mortgageRateLockOutcomeResultValueId.rateLocked]}" even if additional steps remain before closing.

Set the outcome to "${mortgageRateLockOutcomeResultLabel[mortgageRateLockOutcomeResultValueId.rateNotLocked]}" when the borrower was pre-approved and could have locked but did not, and the reason is related to factors the loan officer could have influenced through better engagement, positioning, or sales execution. This includes borrowers who chose another lender after comparing options, borrowers who went cold or unresponsive due to lack of engagement during the shopping phase, borrowers whose real estate agent steered them to a different lender, borrowers who found a home but locked elsewhere because the loan officer was not proactive with offer strategy or rate lock guidance, and borrowers who explicitly cited rate, service, or responsiveness as reasons for not proceeding. When the borrower had the financial ability to proceed and the opportunity was lost due to competitive dynamics, engagement gaps, or sales execution, choose "${mortgageRateLockOutcomeResultLabel[mortgageRateLockOutcomeResultValueId.rateNotLocked]}".

Set the outcome to "${mortgageRateLockOutcomeResultLabel[mortgageRateLockOutcomeResultValueId.disqualified]}" when the borrower did not lock and the primary reason is outside the loan officer's control. This includes borrowers whose financial situation changed after pre-approval (job loss, credit event, new debt that pushed DTI beyond qualifying limits), borrowers who decided not to purchase a home at all due to personal circumstances, borrowers whose pre-approval expired because they could not find a suitable property within the window, and situations where a deal fell apart due to property-side issues such as a failed inspection, seller withdrawal, or an appraisal that killed the transaction before a lock could occur. When the primary reason the borrower did not lock is a qualifying or circumstantial barrier rather than a failure of the loan officer to win the business, choose "${mortgageRateLockOutcomeResultLabel[mortgageRateLockOutcomeResultValueId.disqualified]}".

Do not set "${mortgageRateLockOutcomeResultLabel[mortgageRateLockOutcomeResultValueId.rateLocked]}", "${mortgageRateLockOutcomeResultLabel[mortgageRateLockOutcomeResultValueId.rateNotLocked]}", or "${mortgageRateLockOutcomeResultLabel[mortgageRateLockOutcomeResultValueId.disqualified]}" and instead treat the outcome as "${mortgageRateLockOutcomeResultLabel[mortgageRateLockOutcomeResultValueId.notApplicable]}" (not applicable) if the transcripts reflect a borrower who has not yet been pre-approved and is still in initial qualification or pre-qualification, if the borrower has already locked and the transcripts concern post-lock processing, underwriting, or closing logistics, if the transcripts concern servicing of an existing funded loan, or if the transcripts are too incomplete to determine where the borrower is in the purchase funnel.

`,
    results: [
      {
        category: greenRedYellowResultTypeCategory.green,
        value: mortgageRateLockOutcomeResultValue[mortgageRateLockOutcomeResultValueId.rateLocked],
        label: mortgageRateLockOutcomeResultLabel[mortgageRateLockOutcomeResultValueId.rateLocked]
      },
      {
        category: greenRedYellowResultTypeCategory.red,
        value: mortgageRateLockOutcomeResultValue[mortgageRateLockOutcomeResultValueId.rateNotLocked],
        label: mortgageRateLockOutcomeResultLabel[mortgageRateLockOutcomeResultValueId.rateNotLocked]
      },
      {
        category: greenRedYellowResultTypeCategory.yellow,
        value: mortgageRateLockOutcomeResultValue[mortgageRateLockOutcomeResultValueId.disqualified],
        label: mortgageRateLockOutcomeResultLabel[mortgageRateLockOutcomeResultValueId.disqualified]
      },
      {
        category: greenRedYellowResultTypeCategory.gray,
        value: mortgageRateLockOutcomeResultValue[mortgageRateLockOutcomeResultValueId.notApplicable],
        label: mortgageRateLockOutcomeResultLabel[mortgageRateLockOutcomeResultValueId.notApplicable]
      }
    ]
  },
  {
    type: outcomeDefinitionType.preset,
    id: reverseMortgageOutcomeDefinitionId,
    name: reverseMortgageOutcomeDefinitionName,
    result_type: outcomeResultType.greenRedYellow,
    context: `Set the outcome to "${reverseMortgageOutcomeResultLabel[reverseMortgageOutcomeResultValueId.counselingScheduled]}" when the transcript clearly shows that the borrower has agreed to schedule HUD-required counseling as a result of the call. The transcript should show the loan officer completing a meaningful discovery and financial assessment, presenting one or more reverse mortgage product options (such as HECM, proprietary reverse mortgage programs, or second-lien reverse mortgage products), and the borrower verbally committing to schedule a counseling session, pay the counseling fee, or confirming that they will complete counseling as the next step. If the conversation ends with the loan officer treating counseling as a confirmed next step and the borrower expressing clear agreement or intent to proceed, choose "${reverseMortgageOutcomeResultLabel[reverseMortgageOutcomeResultValueId.counselingScheduled]}", even if the counseling appointment itself has not yet been booked during the call.

Set the outcome to "${reverseMortgageOutcomeResultLabel[reverseMortgageOutcomeResultValueId.counselingNotScheduled]}" when the caller is a plausible reverse mortgage prospect but does not commit to scheduling or completing HUD counseling by the end of the call. The transcript may show financial assessment, benefits analysis, product presentations, or scenario discussions, but the borrower does not clearly agree to move forward with counseling. This includes cases where the borrower wants to think about it, discuss with a spouse or family member, compare with other lenders, asks for a follow-up call, or otherwise declines to commit to counseling during the conversation. It also includes calls where the loan officer presents options but does not ask for the sale or set a clear expectation around counseling as the next step, as well as calls that end with vague or ambiguous next steps.

Set the outcome to "${reverseMortgageOutcomeResultLabel[reverseMortgageOutcomeResultValueId.unqualified]}" when it becomes clear that the borrower cannot proceed with a reverse mortgage because they do not meet basic qualifying criteria for any available product. The transcript should show the loan officer determining that, based on factors such as age, property type, existing lien balances, occupancy status, property value, or program-specific requirements, there is no reverse mortgage product that can be offered or that the borrower cannot qualify at this time. When the primary reason the call does not advance is the borrower's lack of eligibility rather than their choice to delay or decline, choose "${reverseMortgageOutcomeResultLabel[reverseMortgageOutcomeResultValueId.unqualified]}".

Do not set "${reverseMortgageOutcomeResultLabel[reverseMortgageOutcomeResultValueId.counselingScheduled]}", "${reverseMortgageOutcomeResultLabel[reverseMortgageOutcomeResultValueId.counselingNotScheduled]}", or "${reverseMortgageOutcomeResultLabel[reverseMortgageOutcomeResultValueId.unqualified]}" and instead treat the outcome as "${reverseMortgageOutcomeResultLabel[reverseMortgageOutcomeResultValueId.notApplicable]}" (not applicable) if the transcript does not meaningfully concern starting or advancing a new reverse mortgage sale, or if there is not enough information to determine the counseling status. This includes calls that are purely about the status of an existing loan already in process, calls about post-closing or servicing issues, calls with existing borrowers about payment or escrow matters, very short or incomplete transcripts where the purpose of the call is unclear, or early inbound calls where the loan officer provides only general information about reverse mortgages and the borrower's intent to explore specific options is not yet established.

`,
    results: [
      {
        category: greenRedYellowResultTypeCategory.green,
        value: reverseMortgageOutcomeResultValue[reverseMortgageOutcomeResultValueId.counselingScheduled],
        label: reverseMortgageOutcomeResultLabel[reverseMortgageOutcomeResultValueId.counselingScheduled]
      },
      {
        category: greenRedYellowResultTypeCategory.red,
        value: reverseMortgageOutcomeResultValue[reverseMortgageOutcomeResultValueId.counselingNotScheduled],
        label: reverseMortgageOutcomeResultLabel[reverseMortgageOutcomeResultValueId.counselingNotScheduled]
      },
      {
        category: greenRedYellowResultTypeCategory.yellow,
        value: reverseMortgageOutcomeResultValue[reverseMortgageOutcomeResultValueId.unqualified],
        label: reverseMortgageOutcomeResultLabel[reverseMortgageOutcomeResultValueId.unqualified]
      },
      {
        category: greenRedYellowResultTypeCategory.gray,
        value: reverseMortgageOutcomeResultValue[reverseMortgageOutcomeResultValueId.notApplicable],
        label: reverseMortgageOutcomeResultLabel[reverseMortgageOutcomeResultValueId.notApplicable]
      }
    ]
  }
]);
function getDefaultOutcomeResultLabel(outcomeId, resultValue) {
  const presetDefault = defaultOutcomeDefinitions.find((definition) => definition.id === outcomeId)?.results.find((result) => result.value === resultValue)?.label;
  if (presetDefault) {
    return presetDefault;
  }
  if (outcomeId === revenueOutcomeDefinitionId) {
    return revenueOutcomeResults.find((result) => result.value === resultValue)?.label;
  }
  return void 0;
}
function getOutcomeLabelsMissingFromContext(outcomeDefinition) {
  const context = outcomeDefinition.context.toLowerCase();
  return outcomeDefinition.results.filter(
    (result) => result.category !== greenRedYellowResultTypeCategory.gray
  ).map((result) => result.label).filter(
    (label) => label.trim().length > 0 && !context.includes(label.toLowerCase())
  );
}
const gradingOutcomeEntrySchema = z.object({
  value: z.string(),
  commentary: z.string().nullable(),
  extracted_at: z.iso.datetime().optional()
});
const gradingOutcomeSchema = z.object({
  [outcomeDefinitionType.preset]: z.object({
    [winLossOutcomeDefinitionId]: gradingOutcomeEntrySchema,
    [insuranceEnrollmentOutcomeDefinitionId]: gradingOutcomeEntrySchema,
    [insuranceApplicationOutcomeDefinitionId]: gradingOutcomeEntrySchema,
    [mortgageApplicationOutcomeDefinitionId]: gradingOutcomeEntrySchema,
    [mortgageRateLockOutcomeDefinitionId]: gradingOutcomeEntrySchema,
    [supportResolutionOutcomeDefinitionId]: gradingOutcomeEntrySchema,
    [refinancingOutcomeDefinitionId]: gradingOutcomeEntrySchema,
    [qualificationOutcomeDefinitionId]: gradingOutcomeEntrySchema,
    [revenueOutcomeDefinitionId]: gradingOutcomeEntrySchema.extend({
      amount: z.number().nullable().optional()
    }),
    [reverseMortgageOutcomeDefinitionId]: gradingOutcomeEntrySchema,
    [funnelOutcomeDefinitionId]: gradingOutcomeEntrySchema,
    [contactCustomFieldPresenceOutcomeDefinitionId]: gradingOutcomeEntrySchema
  }).partial(),
  [outcomeDefinitionType.custom]: z.record(
    z.uuid(),
    gradingOutcomeEntrySchema.optional()
  )
}).partial();
function getOutcomeValueAndLabel(outcome, outcomeDefinition) {
  if (!outcome) {
    return { value: void 0, label: "None" };
  }
  const rawValue = outcomeDefinition.type === outcomeDefinitionType.preset ? outcome.preset?.[outcomeDefinition.id]?.value : outcome.custom?.[outcomeDefinition.id]?.value;
  if (!rawValue) {
    return { value: void 0, label: "None" };
  }
  const value = resolveLegacyOutcomeValueForDefinition(
    outcomeDefinition,
    rawValue
  );
  const label = outcomeDefinition.results.find((r) => r.value === value)?.label ?? value;
  return { value, label };
}
function getGradingOutcomeEntry(outcome, outcomeDefinition) {
  if (!outcome) {
    return void 0;
  }
  return outcomeDefinition.type === outcomeDefinitionType.preset ? outcome.preset?.[outcomeDefinition.id] : outcome.custom?.[outcomeDefinition.id];
}
function buildGradingOutcome(outcomeDefinition, value) {
  return {
    [outcomeDefinition.type]: {
      [outcomeDefinition.id]: value ? { value, commentary: null } : void 0
    }
  };
}
function getOutcomeValueFromDefinitionAndGrading(outcomeDefinition, outcome) {
  if (!outcome) {
    return void 0;
  }
  const rawValue = outcomeDefinition.type === outcomeDefinitionType.preset ? outcome.preset?.[outcomeDefinition.id]?.value : outcome.custom?.[outcomeDefinition.id]?.value;
  return rawValue ? resolveLegacyOutcomeValueForDefinition(outcomeDefinition, rawValue) : void 0;
}
function getOutcomeResultForValueId(outcomeDefinition, valueId) {
  if (!valueId) {
    return void 0;
  }
  const resolvedValueId = resolveLegacyOutcomeValueForDefinition(
    outcomeDefinition,
    valueId
  );
  return outcomeDefinition.results.find((r) => r.value === resolvedValueId);
}
function getOutcomeCommentaryFromDefinitionAndGrading(outcomeDefinition, outcome) {
  if (!outcome) {
    return null;
  }
  if (outcomeDefinition.type === outcomeDefinitionType.preset) {
    return outcome.preset?.[outcomeDefinition.id]?.commentary ?? null;
  }
  return outcome.custom?.[outcomeDefinition.id]?.commentary ?? null;
}
const outcomeToolOutputSchema = z.object({
  outcome_value: z.string().describe("The outcome value identifier (e.g., win_loss:won)"),
  outcome_label: z.string().describe("The human-readable label for the outcome (e.g., Won, Lost)"),
  outcome_commentary: z.string().nullable().describe("Optional commentary explaining the outcome classification")
}).nullable().optional().describe(
  "The business outcome of the interaction, if the playbook has an outcome configured"
);
function formatOutcomeForToolOutput(outcome, outcomeDefinition) {
  if (!outcomeDefinition || !outcome) {
    return null;
  }
  const value = getOutcomeValueFromDefinitionAndGrading(
    outcomeDefinition,
    outcome
  );
  if (!value) {
    return null;
  }
  const result = getOutcomeResultForValueId(outcomeDefinition, value);
  if (!result) {
    return null;
  }
  const commentary = getOutcomeCommentaryFromDefinitionAndGrading(
    outcomeDefinition,
    outcome
  );
  return {
    outcome_value: value,
    outcome_label: result.label,
    outcome_commentary: commentary
  };
}
const legacyOutcomeValueMap = Object.freeze({
  ...refinancingLegacyValueMap
});
function resolveLegacyOutcomeValue(value) {
  return legacyOutcomeValueMap[value] ?? value;
}
function listOutcomeValueWithLegacyAliases(currentValue) {
  const aliases = Object.entries(legacyOutcomeValueMap).filter(([, resolved]) => resolved === currentValue).map(([legacyValue]) => legacyValue);
  return [currentValue, ...aliases];
}
function resolveLegacyOutcomeValueForDefinition(outcomeDefinition, value) {
  const resolvedValue = resolveLegacyOutcomeValue(value);
  return outcomeDefinition.results.some(
    (result) => result.value === resolvedValue
  ) ? resolvedValue : value;
}
function resolveOutcomeValue(outcomeDefinition, input) {
  if (!input) {
    return void 0;
  }
  const trimmedInput = input.trim();
  const exactMatch = outcomeDefinition.results.find(
    (r) => r.value === trimmedInput
  );
  if (exactMatch) {
    return exactMatch.value;
  }
  const resolvedLegacy = legacyOutcomeValueMap[trimmedInput];
  if (resolvedLegacy) {
    const legacyMatch = outcomeDefinition.results.find(
      (r) => r.value === resolvedLegacy
    );
    if (legacyMatch) {
      return legacyMatch.value;
    }
  }
  const labelMatch = outcomeDefinition.results.find(
    (r) => r.label.toLowerCase() === trimmedInput.toLowerCase()
  );
  if (labelMatch) {
    return labelMatch.value;
  }
  return void 0;
}
function isValidOutcomeValue(outcomeDefinition, value) {
  const resolvedValue = resolveLegacyOutcomeValueForDefinition(
    outcomeDefinition,
    value
  );
  return outcomeDefinition.results.some((r) => r.value === resolvedValue);
}
function getGreenOutcomeValueId(outcomeDefinition) {
  const greenResult = outcomeDefinition.results.find(
    (r) => r.category === "green"
  );
  return greenResult?.value;
}
function getNaOutcomeValueId(outcomeDefinition) {
  const grayResult = outcomeDefinition.results.find(
    (r) => r.category === "gray"
  );
  return grayResult?.value;
}
function getNonNaOutcomeValueIds(outcomeDefinition) {
  return outcomeDefinition.results.filter((r) => r.category !== "gray").map((r) => r.value);
}
function isRevenueOutcomeDefinition(outcomeDefinition) {
  return outcomeDefinition.type === outcomeDefinitionType.preset && outcomeDefinition.id === revenueOutcomeDefinitionId;
}
function getRevenueCustomFieldDefinitionId(outcomeDefinition) {
  return isRevenueOutcomeDefinition(outcomeDefinition) ? outcomeDefinition.custom_field_definition_id : void 0;
}
function isLlmExtractedPresetOutcome(outcomeDefinition) {
  return outcomeDefinition.type === outcomeDefinitionType.preset && !isRevenueOutcomeDefinition(outcomeDefinition) && !isFunnelOutcomeDefinition(outcomeDefinition) && !isContactCustomFieldPresenceOutcomeDefinition(outcomeDefinition);
}
function buildOutcomeExtractionResponseSchema(outcomeDefinition) {
  const possibleValues = outcomeDefinition.results.map(
    (result) => result.value
  );
  const [firstValue, ...restValues] = possibleValues;
  if (firstValue === void 0) {
    throw new Error("Outcome definition must have at least one result.");
  }
  return z.object({
    value: z.enum([firstValue, ...restValues]).describe("The value defining the outcome of the conversations."),
    commentary: z.string().describe(
      "An extremely concise phrase on the reasoning behind the extracted value."
    ).nullable()
  });
}
function getOutcomeAmountFromDefinitionAndGrading(outcomeDefinition, outcome) {
  if (!outcome || !isRevenueOutcomeDefinition(outcomeDefinition)) {
    return null;
  }
  return outcome.preset?.[outcomeDefinition.id]?.amount ?? null;
}
function buildGradingOutcomeWithAmount(outcomeDefinition, value, amount) {
  return {
    [outcomeDefinition.type]: {
      [outcomeDefinition.id]: { value, commentary: null, amount }
    }
  };
}
function mapCustomFieldToRevenueOutcome(customFieldValue) {
  if (customFieldValue === null || customFieldValue === void 0) {
    return {
      value: revenueOutcomeResultValue[revenueOutcomeResultValueId.pending],
      amount: null
    };
  }
  if (customFieldValue <= 0) {
    return {
      value: revenueOutcomeResultValue[revenueOutcomeResultValueId.lost],
      amount: 0
    };
  }
  return {
    value: revenueOutcomeResultValue[revenueOutcomeResultValueId.won],
    amount: customFieldValue
  };
}
export {
  buildContactCustomFieldPresenceOutcomeDefinition,
  buildContactCustomFieldPresenceValue,
  buildFunnelOutcomeDefinition,
  buildFunnelOutcomeValue,
  buildGradingOutcome,
  buildGradingOutcomeWithAmount,
  buildOutcomeExtractionResponseSchema,
  buildOutcomeGreenRateLabel,
  buildOutcomeGreenResultLabel,
  buildOutcomeResultLabel,
  buildRevenueOutcomeDefinition,
  contactCustomFieldPresenceBucketId,
  contactCustomFieldPresenceOutcomeDefinitionId,
  contactCustomFieldPresenceOutcomeDefinitionName,
  defaultOutcomeDefinitions,
  formatOutcomeForToolOutput,
  funnelOutcomeDefinitionId,
  funnelOutcomeDefinitionName,
  funnelOutcomeNotApplicableValue,
  getDefaultOutcomeResultLabel,
  getFunnelStageForRawValue,
  getGradingOutcomeEntry,
  getGreenOutcomeValueId,
  getNaOutcomeValueId,
  getNonNaOutcomeValueIds,
  getOutcomeAmountFromDefinitionAndGrading,
  getOutcomeCommentaryFromDefinitionAndGrading,
  getOutcomeDisplayName,
  getOutcomeLabelsMissingFromContext,
  getOutcomeNoun,
  getOutcomePluralNoun,
  getOutcomeResultForValueId,
  getOutcomeValueAndLabel,
  getOutcomeValueFromDefinitionAndGrading,
  getRevenueCustomFieldDefinitionId,
  gradingOutcomeSchema,
  greenRedYellowResultCategorySchema,
  greenRedYellowResultTypeCategory,
  hasCompleteOutcomeDefinition,
  incompleteOutcomeTerminologyReason,
  isContactCustomFieldPresenceOutcomeDefinition,
  isFunnelOutcomeDefinition,
  isLlmExtractedPresetOutcome,
  isRevenueOutcomeDefinition,
  isValidOutcomeValue,
  listOutcomeValueWithLegacyAliases,
  mapCustomFieldToRevenueOutcome,
  outcomeDefinitionSchema,
  outcomeDefinitionType,
  outcomeGreenRedYellowResultCategoryColor,
  outcomeResultType,
  outcomeToolOutputSchema,
  pluralizeOutcomeNoun,
  qualificationOutcomeDefinitionId,
  refinancingLegacyValueMap,
  resolveLegacyOutcomeValue,
  resolveLegacyOutcomeValueForDefinition,
  resolveOutcomeValue,
  revenueOutcomeDefinitionId,
  revenueOutcomeDefinitionName,
  revenueOutcomeResults,
  supportResolutionOutcomeDefinitionId
};
//# sourceMappingURL=outcome.js.map
